import { getApiUrl, getAuthHeaders, API_CONFIG } from '../config/api';
import { Patient } from '../types';

export interface CreatePatientData {
  email: string;
  password?: string;
  phoneNumber: string;
  fullName: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  emergencyContact?: string;
  medicalHistory?: string;
  allergies?: string;
  currentMedications?: string;
  isActive?: boolean;
}

export interface UpdatePatientData {
  email?: string;
  phoneNumber?: string;
  fullName?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  emergencyContact?: string;
  medicalHistory?: string;
  allergies?: string;
  currentMedications?: string;
  isActive?: boolean;
}

export interface GetPatientsParams {
  page?: number;
  limit?: number;
  isActive?: boolean;
  search?: string;
  gender?: 'male' | 'female' | 'other';
}

export interface PatientsResponse {
  success: boolean;
  data: {
    patients: Patient[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface PatientResponse {
  success: boolean;
  data: Patient;
  message?: string;
}

class PatientService {
  async getPatients(params: GetPatientsParams = {}): Promise<PatientsResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.gender) queryParams.append('gender', params.gender);

    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_PATIENTS}?${queryParams}`), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch patients');
    }

    return response.json();
  }

  async getPatient(id: number): Promise<PatientResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_PATIENT_BY_ID(id)), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch patient');
    }

    return response.json();
  }

  async createPatient(data: CreatePatientData): Promise<PatientResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_PATIENTS), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create patient');
    }

    return response.json();
  }

  async updatePatient(id: number, data: UpdatePatientData): Promise<PatientResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_PATIENT_BY_ID(id)), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update patient');
    }

    return response.json();
  }

  async deletePatient(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_PATIENT_BY_ID(id)), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete patient');
    }

    return response.json();
  }

  async updatePatientStatus(id: number, isActive: boolean): Promise<PatientResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_PATIENT_STATUS(id)), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update patient status');
    }

    return response.json();
  }

  async bulkUpdatePatients(patientIds: number[], updates: UpdatePatientData): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_PATIENTS_BULK), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ patientIds, updates }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to bulk update patients');
    }

    return response.json();
  }
}

export const patientService = new PatientService();
